import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { KPICard, KPIGrid } from '../../components/admin/KPICards';
import { 
  Droplets, Calendar, MapPin, User, Package, Phone, Mail,
  MessageCircle, CheckCircle, Search, Filter, Clock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLIENT_STATUS_CONFIG = {
  activo: { label: 'Activo', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  inactivo: { label: 'Inactivo', color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30' },
  pendiente_revision: { label: 'Pendiente Revisión', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' }
};

function ClientStatusBadge({ status }) {
  const config = CLIENT_STATUS_CONFIG[status] || CLIENT_STATUS_CONFIG.activo;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}>
      {config.label}
    </span>
  );
}

function InstallationCard({ installation }) {
  const handleWhatsApp = () => {
    if (installation.client_phone) {
      const message = encodeURIComponent(`Hola ${installation.client_name}, te contactamos desde Manklar.`);
      window.open(`https://wa.me/${installation.client_phone.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
    }
  };

  return (
    <div 
      className="p-5 rounded-xl bg-white/[0.02] border border-white/10 hover:border-emerald-500/30 transition-all duration-300"
      data-testid={`installation-card-${installation.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <Droplets className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-base font-medium text-white">{installation.client_name}</h3>
            <p className="text-xs text-zinc-500">
              Instalado el {new Date(installation.installed_at).toLocaleDateString('es-ES', { 
                day: 'numeric', month: 'short', year: 'numeric' 
              })}
            </p>
          </div>
        </div>
        <ClientStatusBadge status={installation.status || 'activo'} />
      </div>

      <div className="space-y-2 mb-4">
        {/* Modelo instalado */}
        <div className="flex items-center gap-2 text-sm">
          <Package className="w-3.5 h-3.5 text-zinc-600" />
          <span className="text-white font-medium">{installation.equipment_model || 'MANKLAR TIKAL'}</span>
        </div>
        
        {/* Fecha de instalación destacada */}
        <div className="flex items-center gap-2 text-sm text-zinc-400">
          <Calendar className="w-3.5 h-3.5 text-zinc-600" />
          <span>Fecha: {new Date(installation.installed_at).toLocaleDateString('es-ES', { 
            day: '2-digit', month: '2-digit', year: 'numeric' 
          })}</span>
        </div>
        
        {installation.address && (
          <div className="flex items-center gap-2 text-sm text-zinc-400">
            <MapPin className="w-3.5 h-3.5 text-zinc-600" />
            <span className="truncate">{installation.address}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-sm text-zinc-400">
          <Mail className="w-3.5 h-3.5 text-zinc-600" />
          <span className="truncate">{installation.client_email}</span>
        </div>
        {installation.referrer_name && (
          <div className="flex items-center gap-2 text-sm text-zinc-400">
            <User className="w-3.5 h-3.5 text-zinc-600" />
            <span>Referido por {installation.referrer_name}</span>
          </div>
        )}
      </div>

      {/* Comercial responsable */}
      {installation.commercial_agent && (
        <div className="mb-4 p-2 rounded bg-white/5 text-xs text-zinc-400 flex items-center gap-2">
          <User className="w-3 h-3" />
          <span>Comercial: <span className="text-white">{installation.commercial_agent}</span></span>
        </div>
      )}

      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleWhatsApp}
          className="flex-1 h-9 bg-[#25D366]/10 border-[#25D366]/30 text-[#25D366] hover:bg-[#25D366]/20"
        >
          <MessageCircle className="w-4 h-4 mr-1" />
          Contactar
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.open(`tel:${installation.client_phone?.replace(/\s/g, '')}`)}
          className="h-9 bg-transparent border-white/10 text-zinc-300 hover:bg-white/10"
        >
          <Phone className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default function AdminInstallations() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  const [installations, setInstallations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterModel, setFilterModel] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const [installRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/installations`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/installations/stats`, { withCredentials: true })
      ]);
      setInstallations(installRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching installations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Filter installations
  const filteredInstallations = installations.filter(inst => {
    const query = searchQuery.toLowerCase().trim();
    const matchesSearch = !query || 
      inst.client_name?.toLowerCase().includes(query) ||
      inst.client_email?.toLowerCase().includes(query) ||
      inst.commercial_agent?.toLowerCase().includes(query);
    const matchesModel = filterModel === 'all' || inst.equipment_model === filterModel;
    return matchesSearch && matchesModel;
  });

  // Get unique models for filter
  const uniqueModels = [...new Set(installations.map(i => i.equipment_model).filter(Boolean))];

  if (loading) {
    return (
      <AdminLayout
        title="Instalaciones"
        subtitle="Cargando..."
        onLogout={handleLogout}
      >
        <div className="flex items-center justify-center h-96">
          <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Instalaciones"
      subtitle={`${installations.length} equipos instalados`}
      onLogout={handleLogout}
    >
      {/* Stats */}
      <KPIGrid className="mb-8">
        <KPICard
          title="Total Instalaciones"
          value={stats?.total || 0}
          icon={Droplets}
          variant="accent"
        />
        <KPICard
          title="Clientes Activos"
          value={stats?.activo || 0}
          icon={CheckCircle}
          variant="success"
        />
        <KPICard
          title="Este Mes"
          value={stats?.this_month || 0}
          icon={Calendar}
          variant="highlight"
        />
      </KPIGrid>

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
          {/* Search */}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              type="text"
              placeholder="Buscar cliente, comercial..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9 bg-white/5 border-white/10 text-white text-sm placeholder:text-zinc-500"
              data-testid="installations-search-input"
            />
          </div>
          
          {/* Model Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-500" />
            <Select value={filterModel} onValueChange={setFilterModel}>
              <SelectTrigger className="w-48 h-9 bg-white/5 border-white/10 text-white text-sm" data-testid="installations-model-filter">
                <SelectValue placeholder="Filtrar por modelo" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="all" className="text-zinc-300">Todos los modelos</SelectItem>
                {uniqueModels.map((model) => (
                  <SelectItem key={model} value={model} className="text-zinc-300">
                    {model}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-sm text-zinc-500">{filteredInstallations.length} resultado{filteredInstallations.length !== 1 ? 's' : ''}</p>
      </div>

      {/* Installations Grid */}
      {filteredInstallations.length === 0 ? (
        <div className="p-12 rounded-xl text-center bg-white/[0.02] border border-white/10">
          <Droplets className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-lg text-white mb-2 font-['Outfit']">
            No hay instalaciones
          </h3>
          <p className="text-sm text-zinc-500">
            Las instalaciones aparecerán aquí cuando cambies el estado de una solicitud a "Instalado"
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredInstallations.map(installation => (
            <InstallationCard key={installation.id} installation={installation} />
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
